import { useQuery } from "@tanstack/react-query";
import { fetchAllPaginated } from "@/lib/supabase-paginate";

export interface ProjectProgress {
  /** Sum of assigned hours on approved/completed tasks (internal only) — tooltip */
  approvedHours: number;
  /** Sum of assigned hours on ALL internal tasks — tooltip */
  assignedHours: number;
  /** Total budgeted hours (internal only) — for tooltip context */
  budgetedHours: number;
  /** VIB-943: antall godkjente/fullførte interne oppgaver (teller for pct) */
  approvedTasks: number;
  /** VIB-943: antall tildelte interne oppgaver totalt (nevner for pct) */
  totalTasks: number;
  /** approvedHours / budgetedHours * 100, capet på 100 (0 hvis ingen budsjett) */
  pct: number;
}

/**
 * Fetch project progress for all projects.
 *
 * Progress = sum tildelte timer på godkjente/fullførte oppgaver / totalt
 * BUDSJETTERTE (solgte) timer (Erik 12.06, f.eks. 8t godkjent / 34t solgt = 24 %).
 * Time-vektet, men med budsjetterte timer som nevner (ikke tildelte) — det
 * unngår VIB-943-fellen der approvedHours/assignedHours ga falsk 100 % når kun
 * de timesatte oppgavene var godkjent. approvedTasks/totalTasks beholdes for
 * tooltip-kontekst.
 *
 * Returns a Map<projectId, ProjectProgress>.
 */
async function fetchProjectProgress(): Promise<Map<string, ProjectProgress>> {
  // 1. Alle interne assignments — også de som ikke er ferdig — for å bygge
  //    nevneren. Status filtreres på klient-siden så vi får både approved/
  //    completed (teller) og resten (kun nevner).
  // Begge er team-brede reads som vokser mot 1000-rad-taket (assignments ~640,
  // budget_lines ~600) — paginert så progressbarene ikke stopper stille. VIB-1249.
  const assignments = await fetchAllPaginated<{
    hours: number | null;
    task_status: string | null;
    budget_line_id: string | null;
    budget_lines: { project_id: string | null; cost_type: string | null } | null;
  }>(
    "budget_line_assignments",
    "hours, task_status, budget_line_id, budget_lines(project_id, cost_type)",
  );

  // 2. All internal budget lines for budsjetterte timer (tooltip-tekst)
  const budgetLines = await fetchAllPaginated<{
    project_id: string | null;
    quantity_hours: number | null;
  }>("budget_lines", "project_id, quantity_hours", (q) =>
    q.eq("cost_type", "internal"),
  );

  const map = new Map<string, ProjectProgress>();
  const empty = (): ProjectProgress => ({
    approvedHours: 0,
    assignedHours: 0,
    budgetedHours: 0,
    approvedTasks: 0,
    totalTasks: 0,
    pct: 0,
  });

  // Sum budgeted hours per project
  for (const bl of budgetLines) {
    if (!bl.project_id) continue;
    const entry = map.get(bl.project_id) ?? empty();
    entry.budgetedHours += bl.quantity_hours ?? 0;
    map.set(bl.project_id, entry);
  }

  // Sum assigned hours per project (internal only), separer ut approved-andelen
  for (const a of assignments) {
    const bl = a.budget_lines;
    if (!bl || bl.cost_type === "external") continue;
    const projectId = bl.project_id as string;
    if (!projectId) continue;

    const entry = map.get(projectId) ?? empty();
    const hours = Number(a.hours ?? 0);
    const isApproved = a.task_status === "approved" || a.task_status === "completed";
    // Timer beholdes for tooltip; pct beregnes på antall oppgaver.
    entry.assignedHours += hours;
    entry.totalTasks += 1;
    if (isApproved) {
      entry.approvedHours += hours;
      entry.approvedTasks += 1;
    }
    map.set(projectId, entry);
  }

  // Progresjon er TIME-vektet på BUDSJETTERTE (solgte) timer (Erik 12.06):
  // sum tildelte timer på godkjente/fullførte oppgaver / totalt solgte timer.
  // Nevneren er budsjetterte timer (ikke tildelte), så den unngår VIB-943-fellen
  // (falsk 100% når kun de timesatte oppgavene var godkjent) — uferdig tildeling
  // gir lavere progresjon, ikke kunstig høy. Capet på 100%.
  for (const [, entry] of map) {
    entry.pct = entry.budgetedHours > 0
      ? Math.min(100, Math.round((entry.approvedHours / entry.budgetedHours) * 100))
      : 0;
  }

  return map;
}

export function useProjectProgress() {
  return useQuery({
    queryKey: ["project-progress"],
    queryFn: fetchProjectProgress,
    staleTime: 60_000,
  });
}
